import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from "@mui/material";
import { Colleague } from "../../../features/Connections/types/Colleague.type";
import { useRemoteFetchFleetBlockedUsers } from "../../../hooks/Remote/Fleet/useRemoteFetchFleetBlockedUsers";
import { useRemoteSaveFleetBlockedUsers } from "../../../hooks/Remote/Fleet/useRemoteSaveFleetBlockedUsers";

export type BlockedUsersDialogProps = {
  vehicleId: number | null;
  vehicleName: string;
  allUsers: Colleague[] | undefined;
  onClose: () => void;
};

export function BlockedUsersDialog({
  vehicleId,
  vehicleName,
  allUsers,
  onClose
}: Readonly<BlockedUsersDialogProps>) {
  const { t } = useTranslation();
  const open = vehicleId != null;

  const { data: blockedUsersData, isLoading } = useRemoteFetchFleetBlockedUsers(vehicleId);
  const { mutateAsync: saveFleetBlockedUsers, isLoading: isSaving } =
    useRemoteSaveFleetBlockedUsers();

  const options = Array.isArray(allUsers) ? allUsers : [];
  const [selectedUsers, setSelectedUsers] = useState<Colleague[]>([]);

  useEffect(() => {
    if (!open || isLoading) return;

    const blocked = blockedUsersData ?? [];
    const opts = Array.isArray(allUsers) ? allUsers : [];
    setSelectedUsers(opts.filter(u => blocked.some(b => b.userId === u.userId)));
  }, [open, isLoading, blockedUsersData, allUsers]);

  const handleClose = () => {
    setSelectedUsers([]);
    onClose();
  };

  const handleConfirm = async () => {
    if (vehicleId == null) {
      onClose();
      return;
    }

    await saveFleetBlockedUsers({
      vehicleId,
      userIds: selectedUsers.map(u => u.userId)
    });

    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth keepMounted>
      <DialogTitle>
        {t("Blocked Users")} – {vehicleName}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Autocomplete
          multiple
          fullWidth
          disableCloseOnSelect
          loading={isLoading}
          options={options}
          getOptionLabel={option => `${option.firstName} ${option.surname}`} // (${option.email})
          getOptionKey={option => option.userId}
          value={selectedUsers}
          onChange={(_, value) => setSelectedUsers(value)}
          renderInput={params => (
            <TextField
              {...params}
              label={t("Select users to block from booking this vehicle")}
              placeholder={t("Search users...")}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...rest } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...rest}
                  label={`${option.firstName} ${option.surname}`}
                  size="small"
                />
              );
            })
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" data-testid="blocked-users-dialog-cancel">
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSaving}
          data-testid="blocked-users-dialog-confirm"
        >
          {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
